import { Link } from 'react-router-dom';
import {
  Award, Heart, ShieldCheck, Lightbulb, Target, Eye, Home as HomeIcon, Building2,
  Trophy, BadgeCheck, Download, Sparkles, ArrowRight, MapPin, Users, Landmark
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSite } from '../context/SiteContext';
import { getText } from '../lib/siteContent';

const PROFILE_PDF = '/Terra-Company-Profile.pdf';

const CORE_VALUES = [
  { icon: Heart, title: 'Customer Focus', desc: 'Prioritizing the needs and satisfaction of our clients.' },
  { icon: ShieldCheck, title: 'Integrity', desc: 'Upholding honesty and transparency in all our dealings.' },
  { icon: Award, title: 'Excellence', desc: 'Striving for the highest standards in service and professionalism.' },
  { icon: Lightbulb, title: 'Innovation', desc: 'Embracing new technologies and strategies to enhance the real estate experience.' },
];

const SERVICES = [
  { icon: HomeIcon, title: 'Residential Sales', desc: 'Assisting clients in buying and selling homes with market analysis and negotiation expertise.' },
  { icon: Building2, title: 'Commercial Real Estate', desc: 'Providing guidance in the acquisition and leasing of commercial properties.' },
];

const LEADERS = [
  { name: 'Adrian S. Ramos', role: 'Sales Head', creds: 'REB, CPA · 18 yrs in RE Sales · ~₱5B in Amaia sales' },
  { name: 'Jay Rizalino Dilag', role: 'Sales Manager — Team Olympus', creds: 'SM Expressive Realty, SM Amaia, SM FLI · 20 yrs in Real Estate' },
  { name: 'Lara Michelle Liwag', role: 'Sales Manager — Team Excellence', creds: 'Sales Director, FLI · 10 yrs in Real Estate' },
  { name: 'John Eron Parohinog', role: 'Sales Manager — Team Thunder', creds: 'SM Phirst, SM FLI · 8 yrs in Real Estate' },
  { name: 'Judith B. Rico', role: 'Sales Manager — Team Horizon', creds: 'REB, SM Phirst · 8 yrs in Real Estate' },
  { name: 'Pau Tomelden', role: 'Finance & Admin', creds: 'Operations, finance & administration' },
];

const TEAMS = [
  { name: 'Team Thunder', lead: 'John Eron Parohinog', members: ['Jonas Dalawatan', 'Emma Villas', 'Kieth Daniel Tubongbanua', 'Lorenzo Latonera', 'Lyza Parohinog', 'Hazel Elaine Madrid', 'Christina Lunar', 'Teresita Spanswick'] },
  { name: 'Team Horizon', lead: 'Judith Rico', members: ['Jennifer Martinez', 'Gersom Carbonel', 'Ida Dizon'] },
  { name: 'Team Excellence', lead: 'Lara Michelle Liwag', members: ['Mark Anthony Liwag', 'Gretchen Rosal', 'Jonathan Bacan', 'Jennelyn Reyes', 'Francis Bandola', 'Jerald Manaig', 'Jhell Ann Tumpalan', 'Crezy Delos Santos'] },
  { name: 'Team Olympus', lead: 'Jay Rizalino Dilag', members: ['Christine Angeles', 'Ann Corin Alcazar', 'Eric Lumibao', 'Jen Tagura'] },
];

const ACCREDITATIONS = ['Phirst Park Homes', 'Filinvest', 'Century Properties', 'Cathay Land Inc.', 'Moldex', 'Camella', 'Lessandra', 'Raemulan', 'Pueblo De Oro', 'PA Properties'];

const AWARDS = [
  'Top 2 Broker Realty in Reservation & Booking, FY 2025 — Phirst Park Homes',
  'Top 2 Broker Realty in Reservation & Booking, Q1 2026 — Phirst Park Homes',
  'Top 2 Broker Realty, Q4 2025 — Filinvest Cavite Channel',
  'Top 2 Area Manager, Q4 2025 — Filinvest Cavite Channel (Lara Michelle Liwag)',
  'Top 1 Seller, Q4 2025 — Filinvest Cavite Channel (Mark Anthony Liwag)',
  'Certificates of Recognition — Moldex, Pueblo De Oro & Camella (FY 2025)',
];

const REGISTRATION = [
  ['Company Name', 'TERRA REALTY OPC'],
  ['SEC Reg. No.', '2025100223850-08'],
  ['Business Permit No.', '13178'],
  ['TIN No.', '696-568-306-00000'],
  ['Registered Address', 'Brgy. Canlubang, Calamba City, Laguna 4028'],
  ['Registered Date', 'February 14, 2025'],
  ['Started Operating', 'October 20, 2024 (as TerraHomes Realty)'],
];

const STATS = [
  { value: '10+', label: 'Accredited Developers' },
  { value: '25+', label: 'Real Estate Advisors' },
  { value: '₱5B+', label: 'Career Sales Volume' },
];

const initials = (n) => n.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const Eyebrow = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-wider text-[#DC2626]">{children}</span>
);

export default function About() {
  const { settings } = useSite();
  const t = (key) => getText(settings, key);
  const name = settings?.companyName || 'Terra Homes Realty';
  const story = settings?.aboutStory || 'TERRA REALTY OPC is a premier group of real estate advisors based in Calamba, Laguna, dedicated to exceptional service and expertise in helping clients achieve their real estate goals. Operating since October 2024 and SEC-registered in February 2025, our team brings decades of combined experience across the country’s leading developers.';
  const hero = settings?.heroImage || 'https://images.unsplash.com/photo-1598258710957-db8614c2881e?auto=format&fit=crop&w=1600&q=80';
  const skyline2 = 'https://images.unsplash.com/photo-1596564309076-01c1868b07a4?auto=format&fit=crop&w=1600&q=80';

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative h-[520px] overflow-hidden">
        <img src={hero} alt="Philippine skyline" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 h-full flex flex-col justify-center text-white">
          <Eyebrow>About Us</Eyebrow>
          <h1 className="text-4xl md:text-6xl font-bold mt-2 max-w-3xl">{t('about_hero_title')}</h1>
          <p className="mt-3 text-lg text-neutral-200 max-w-2xl">{t('about_hero_subtitle')}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href={PROFILE_PDF} download>
              <Button className="btn-primary h-12 px-6 gap-2"><Download className="w-5 h-5" /> Download Company Profile</Button>
            </a>
            <Link to="/contact?subject=agent">
              <Button variant="outline" className="h-12 px-6 bg-transparent border-white text-white hover:bg-white hover:text-neutral-900">Join Our Team</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* WHAT TERRA MEANS */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-8">
          <Eyebrow>What Our Name Means</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-1">TERRA HOMES</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-8 text-center">
            <Landmark className="w-9 h-9 text-[#DC2626] mx-auto mb-3" />
            <p className="text-xs uppercase tracking-widest text-neutral-500">Terra <span className="italic normal-case">(Latin)</span></p>
            <p className="text-2xl font-bold text-neutral-900 mt-2">Earth · Land · Territory</p>
          </div>
          <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-8 text-center">
            <HomeIcon className="w-9 h-9 text-[#DC2626] mx-auto mb-3" />
            <p className="text-xs uppercase tracking-widest text-neutral-500">Home</p>
            <p className="text-2xl font-bold text-neutral-900 mt-2">A place where our family lives</p>
          </div>
        </div>
      </section>

      {/* OUR COMPANY / STORY */}
      <section className="max-w-7xl mx-auto px-6 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <img src={skyline2} alt="Calamba, Laguna" className="rounded-xl w-full h-[420px] object-cover shadow-lg" />
        <div>
          <Eyebrow>Our Company</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-1">{t('about_story_title')}</h2>
          <p className="mt-4 text-neutral-700 leading-relaxed">{story}</p>
          <p className="mt-3 text-neutral-700 leading-relaxed">Our promise stays the same: <strong>{settings?.tagline || 'Your Home, Our Goal'}</strong>. From your first viewing to handover day, we are with you every step of the way.</p>
          <div className="mt-7 grid grid-cols-3 gap-4">
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="text-3xl font-bold text-[#DC2626]">{s.value}</p>
                <p className="text-xs text-neutral-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="bg-neutral-50 py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white p-8 rounded-xl border border-neutral-200">
            <Target className="w-10 h-10 text-[#DC2626] mb-3" />
            <h3 className="text-2xl font-bold text-neutral-900">Mission</h3>
            <p className="mt-3 text-neutral-700 leading-relaxed">{t('about_mission')}</p>
          </div>
          <div className="bg-white p-8 rounded-xl border border-neutral-200">
            <Eye className="w-10 h-10 text-[#DC2626] mb-3" />
            <h3 className="text-2xl font-bold text-neutral-900">Vision</h3>
            <p className="mt-3 text-neutral-700 leading-relaxed">{t('about_vision')}</p>
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Eyebrow>Our Core Values</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-1">{t('about_values_title')}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CORE_VALUES.map((v) => {
            const Icon = v.icon;
            return (
              <div key={v.title} className="p-6 rounded-xl border border-neutral-200 bg-white card-hover">
                <div className="inline-flex w-12 h-12 rounded-full bg-red-50 text-[#DC2626] items-center justify-center mb-3"><Icon className="w-6 h-6" /></div>
                <h3 className="font-bold text-neutral-900">{v.title}</h3>
                <p className="text-sm text-neutral-600 mt-1">{v.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* SERVICES + TARGET MARKET */}
      <section className="bg-neutral-950 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-10">
            <Eyebrow>What We Do</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-bold mt-1">Services Offered</h2>
            <p className="text-neutral-400 mt-2">We cater to individuals, families, and investors looking to buy, sell, or lease residential and commercial properties across the Philippines.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="bg-neutral-900 border border-neutral-800 rounded-xl p-7">
                  <div className="inline-flex w-12 h-12 rounded-lg bg-[#DC2626]/90 items-center justify-center mb-4"><Icon className="w-6 h-6" /></div>
                  <h3 className="text-xl font-bold">{s.title}</h3>
                  <p className="text-neutral-400 mt-2 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Eyebrow>Leadership & Experience</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-1">The team behind Terra</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {LEADERS.map((l) => (
            <div key={l.name} className="p-6 rounded-xl border border-neutral-200 bg-white card-hover flex gap-4">
              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-[#DC2626] to-[#991B1B] text-white flex items-center justify-center font-bold text-lg">{initials(l.name)}</div>
              <div>
                <p className="font-bold text-neutral-900 leading-tight">{l.name}</p>
                <p className="text-xs font-semibold text-[#DC2626] mt-0.5">{l.role}</p>
                <p className="text-sm text-neutral-600 mt-2 leading-snug">{l.creds}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TEAMS ROSTER */}
      <section className="bg-neutral-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Eyebrow>Our Advisors</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-1">Four teams, one mission</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TEAMS.map((team) => (
              <div key={team.name} className="bg-white rounded-xl border border-neutral-200 p-6">
                <div className="flex items-center gap-2 mb-1"><Users className="w-4 h-4 text-[#DC2626]" /><h3 className="font-bold text-neutral-900">{team.name}</h3></div>
                <p className="text-xs text-neutral-500 mb-4">Led by SM {team.lead}</p>
                <ul className="space-y-1.5">
                  {team.members.map((m) => (
                    <li key={m} className="text-sm text-neutral-700 flex items-start gap-2"><span className="text-[#DC2626] mt-0.5">•</span> {m}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ACCREDITATIONS */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <Eyebrow>Trusted By</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-1">Developer Accreditations</h2>
          <p className="text-neutral-600 mt-2">Accredited to sell for the Philippines’ most respected developers.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          {ACCREDITATIONS.map((a) => (
            <span key={a} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-neutral-200 bg-white text-neutral-800 font-medium shadow-sm">
              <BadgeCheck className="w-4 h-4 text-[#DC2626]" /> {a}
            </span>
          ))}
          <span className="inline-flex items-center px-5 py-2.5 rounded-full bg-neutral-100 text-neutral-500 font-medium">and more</span>
        </div>
      </section>

      {/* AWARDS */}
      <section className="bg-neutral-950 text-white py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <Eyebrow>Awards & Recognition</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-bold mt-1">Recognized for results</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {AWARDS.map((a) => (
              <div key={a} className="flex items-start gap-3 bg-neutral-900 border border-neutral-800 rounded-xl p-5">
                <Trophy className="w-5 h-5 text-[#DC2626] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-neutral-200 leading-snug">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REGISTRATION */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-8">
          <Eyebrow>Company Registration</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-1">Officially registered & compliant</h2>
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-white overflow-hidden">
          <dl className="divide-y divide-neutral-100">
            {REGISTRATION.map(([k, v]) => (
              <div key={k} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 px-6 py-4">
                <dt className="w-full sm:w-56 flex items-center gap-2 text-sm font-semibold text-neutral-500 flex-shrink-0"><MapPin className="w-3.5 h-3.5 text-[#DC2626]" /> {k}</dt>
                <dd className="text-neutral-900 font-medium">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="text-center mt-8">
          <a href={PROFILE_PDF} download>
            <Button className="btn-primary h-12 px-6 gap-2"><Download className="w-5 h-5" /> Download Full Company Profile (PDF)</Button>
          </a>
        </div>
      </section>

      {/* JOIN US CTA */}
      <section className="relative py-20 overflow-hidden">
        <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-neutral-950/85" />
        <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
          <Sparkles className="w-10 h-10 text-[#DC2626] mx-auto mb-3" />
          <h2 className="text-3xl md:text-5xl font-bold">Join us now — build your future in real estate</h2>
          <p className="mt-3 text-lg text-neutral-300 max-w-2xl mx-auto">Experience the perks and benefits of a career in real estate. For your dreams. For the family.</p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Link to="/contact?subject=agent"><Button className="btn-primary h-12 px-6 gap-2">Become an Advisor <ArrowRight className="w-4 h-4" /></Button></Link>
            <a href={PROFILE_PDF} download><Button variant="outline" className="h-12 px-6 bg-transparent border-white text-white hover:bg-white hover:text-neutral-900 gap-2"><Download className="w-4 h-4" /> Company Profile</Button></a>
          </div>
        </div>
      </section>
    </div>
  );
}
